<template>
  <div id="edssscale" :class="enlargeScale ? 'large-scale' : ''">
    <div class="bars-container">
      <div
        v-for="(item, idx) in bars.slice().reverse()"
        :key="idx"
        class="bar-container"
      >
        <div class="scale-label">{{ item.label }}</div>

        <div class="bar">
          <div class="fill" :style="{ width: `${item.width}%` }">
            <div
              class="color"
              :style="{ background: getColor((idx - 4) / (bars.length - 4)) }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <EdssIcon
      ref="edssIcon"
      :show-nub="true"
      :year="parseInt(this.$store.state.year, 10)"
      :class="enlargeScale ? 'large-scale' : ''"
      :show-score="enlargeScale ? true : false"
    />
  </div>
</template>

<script>
import TweenMax from "gsap";

import EdssIcon from "@/components/EdssIcon.vue";
import yearBarIdxMapping from "../yearBarIdxMapping.js";
import lerpColor from "../lerpColor.js";

export default {
  name: "EdssScale",
  components: {
    EdssIcon,
  },
  props: {
    enlargeScale: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currIdx: 0,
      ready: true,
      bars: [
        { label: 0.0, width: 17.5 },
        { label: 1.0, width: 19.5 },
        { label: 1.5, width: 24 },
        { label: 2.0, width: 28.4 },
        { label: 2.5, width: 32.9 },
        { label: 3.0, width: 37 },
        { label: 3.5, width: 40.8 },
        { label: 4.0, width: 44.7 },
        { label: 4.5, width: 48 },
        { label: 5.0, width: 52.8 },
        { label: 5.5, width: 54.9 },
        { label: 6.0, width: 61.7 },
        { label: 6.5, width: 64.4 },
        { label: 7.0, width: 69.5 },
        { label: 7.5, width: 72.8 },
        { label: 8.0, width: 78.8 },
        { label: 8.5, width: 82 },
        { label: 9.0, width: 88 },
        { label: 9.5, width: 94 },
        { label: 10, width: 100 },
      ],
    };
  },
  methods: {
    getColor: function (v) {
      return lerpColor(v);
    },

    updateIcon: function (year, idx, t) {
      this.$refs.edssIcon.updateIcon(year, idx, t);
    },

    tweenBackward: function (v, t = 0.5) {
      if (this.ready == false) {
        return;
      }

      let that = this;

      function getElsForTween(className) {
        let collection = document.getElementsByClassName(className);
        let els = [...collection];
        return els.slice(0, that.bars.length - v - 1);
      }

      let targets = getElsForTween("color");
      TweenMax.to(targets, t, {
        duration: 0.01,
        stagger: 0.01,
        width: "0%",
        onStart: function () {
          that.ready = false;
        },
        onComplete: function () {
          that.ready = true;
          that.currIdx = v;
        },
      });

      let labelTargets = getElsForTween("scale-label");
      TweenMax.to(labelTargets, t, {
        duration: 0.01,
        stagger: 0.01,
        color: function () {
          return "white";
        },
      });
    },

    doTween() {
      if (this.ready === false) return;

      let year = this.$store.state.year;
      let barIdx = yearBarIdxMapping[year];

      if (barIdx < this.currIdx) {
        this.tweenBackward(barIdx);
      } else {
        this.tweenForward(barIdx);
      }
      this.currIdx = barIdx;

      this.$refs.edssIcon.updateIcon(year, this.currIdx, 0.5);
    },

    init: function (year) {
      let barIdx = yearBarIdxMapping[year];

      if (this.ready == false) {
        return;
      }
      let that = this;

      function getElsForTween(className) {
        let labelCollection = document.getElementsByClassName(className);
        let labelEls = [...labelCollection].reverse();
        return labelEls.slice(0, barIdx + 1);
      }

      let labelTargets = getElsForTween("scale-label");
      TweenMax.to(labelTargets, 0, {
        duration: 0,
        color: function (index) {
          return that.getColor(1 - index / (that.bars.length - 4));
        },
      });

      let targets = getElsForTween("color");
      // conditional just to prevent a warning
      if (targets.length > 0) {
        TweenMax.to(targets, 0, {
          duration: 0,
          width: "100%",
          onStart: function () {
            that.ready = false;
          },
          onComplete: function () {
            that.ready = true;
            that.currIdx = barIdx;
          },
        });
      }
    },

    tweenForward: function (v, t = 0.5) {
      if (this.ready == false) {
        return;
      }
      let that = this;

      function getElsForTween(className) {
        let labelCollection = document.getElementsByClassName(className);
        let labelEls = [...labelCollection].reverse();
        return labelEls.slice(0, v + 1);
      }

      let labelTargets = getElsForTween("scale-label");
      TweenMax.to(labelTargets, t, {
        duration: 0,
        stagger: 0,
        color: function (index) {
          return that.getColor(1 - index / (that.bars.length - 4));
        },
      });

      let targets = getElsForTween("color");
      // conditional just to prevent a warning
      if (targets.length > 0) {
        TweenMax.to(targets, t, {
          duration: 0.01,
          stagger: 0.01,
          width: "100%",
          onStart: function () {
            that.ready = false;
          },
          onComplete: function () {
            that.ready = true;
            that.currIdx = v;
          },
        });
      }
    },
  },

  beforeDestroy() {
    clearInterval(this.interval);
  },

  mounted() {
    this.init(this.$store.state.year);

    this.interval = setInterval(() => {
      this.doTween();
    }, 100);
  },
};
</script>

<style lang="scss">
#edssscale {
  display: flex;
  position: relative;
  margin-top: rem(20px);
}

.bar-container {
  height: 20px;
  width: 125px;
  @include breakpoint(xs) {
    // width: 100px;
    // padding-left: rem(10px);
  }
  .scale-label {
    color: white;
    font-weight: bold;
    display: inline-block;
    margin-right: 10px;
    width: 30px;
    text-align: right;
  }

  .bar {
    display: inline-block;
    height: 15px;
    width: 70px;
    background: rgba(100, 100, 100, 0.5);

    .fill {
      height: 100%;
      background: rgba(200, 200, 200, 0.25);

      .color {
        height: 100%;
        width: 0%;
        background: rgba(0, 0, 100, 1);
      }
    }
  }
}

.bar-container:nth-child(20) {
  height: 15px;
}

.bar-container:nth-child(-n + 4) {
  opacity: 0.25;
}

// Issue #47: Realized that the tweens are dependent on the existing styles so increasing the height of the bars throws off the transitions...commenting out for now + just keeping the same scale size between Mobility + Disease Prog. pages.

// "ZOOMED IN" Mobility page styles
// #edssscale.large-scale {
//   margin-top: 0;

//   .bar-container {
//     height: 25px;
//     width: 145px;
//     display: flex;
//     align-items: center;
//     flex-basis: auto;

//     .bar {
//       height: 20px;
//       width: 100px;
//     }
//   }

//   .bar-container:nth-child(20) {
//     height: 25px;
//   }
// }
</style>